/**
 * Realtime PartyServer da conversa ativa: mensagens, updates e typing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createModuleLogger } from '../../../utils/logger';
import {
    PARTYKIT_CHAT_DEBOUNCE_MS,
    canUsePartykit,
    safeClosePartySocket,
    claimAuthedPartySocket,
    connectPartySocket,
    attachPartykitVisibilityReconnect,
    createPartykitCooldownController,
    attachPartySocketHandlers,
} from '../utils/partykitSocket';
import type PartySocket from 'partysocket';

const log = createModuleLogger('[PartyRealtime]');
const TYPING_THROTTLE_MS = 2000;
const PEER_TYPING_TTL_MS = 3000;

export interface PartyMessagePayload {
    type: 'CONNECTED' | 'NEW_MESSAGE' | 'MESSAGE_UPDATE' | 'TYPING';
    conversationId?: string;
    messageId?: string;
    messageTimestamp?: string;
    content?: string;
    fromMe?: boolean;
    messageType?: string;
    status?: string;
}

function defer(fn: () => void): void {
    setTimeout(fn, 0);
}

export function usePartyRealtime(
    conversationDbId: string | null,
    onMessageReceived: (payload?: PartyMessagePayload) => void,
    options: { enabled?: boolean } = {},
) {
    const { enabled = true } = options;
    const [isConnected, setIsConnected] = useState(false);
    const [isPeerTyping, setIsPeerTyping] = useState(false);
    const socketRef = useRef<PartySocket | null>(null);
    const onMessageReceivedRef = useRef(onMessageReceived);
    onMessageReceivedRef.current = onMessageReceived;
    const lastTypingSentRef = useRef(0);
    const peerTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const sendTyping = useCallback(() => {
        const socket = socketRef.current;
        if (!socket || !conversationDbId) return;
        const now = Date.now();
        if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
        lastTypingSentRef.current = now;
        try {
            socket.send(JSON.stringify({
                type: 'TYPING',
                conversationId: conversationDbId,
                timestamp: new Date().toISOString(),
            }));
        } catch { /* ignore */ }
    }, [conversationDbId]);

    useEffect(() => {
        setIsConnected(false);
        setIsPeerTyping(false);
        lastTypingSentRef.current = 0;
        if (!enabled || !conversationDbId) {
            safeClosePartySocket(socketRef.current);
            socketRef.current = null;
            setIsPeerTyping(false);
            return;
        }

        let cancelled = false;
        let connectAttempt = 0;

        const connectSocket = () => {
            if (cancelled || !canUsePartykit('chat')) return;

            const attempt = ++connectAttempt;
            safeClosePartySocket(socketRef.current);
            socketRef.current = null;

            void (async () => {
                try {
                    const socket = await claimAuthedPartySocket(conversationDbId, 'chat', {
                        isCancelled: () => cancelled,
                        attempt,
                        latestAttempt: () => connectAttempt,
                    });
                    if (!socket) return;
                    socketRef.current = socket;
                    attachPartySocketHandlers(socket, 'chat', () => cancelled, {
                        onOpen: () => defer(() => {
                            if (cancelled) return;
                            setIsConnected(true);
                            // PartySocket reconecta sem replay — recupera mensagens perdidas via DB.
                            onMessageReceivedRef.current();
                        }),
                        onClose: () => defer(() => {
                            if (!cancelled) {
                                setIsConnected(false);
                                setIsPeerTyping(false);
                            }
                        }),
                        armCooldown: () => cooldown.armAfterFailure(),
                        onMessage: (raw) => {
                            try {
                                const data = JSON.parse(raw) as PartyMessagePayload;
                                if (data.type === 'TYPING') {
                                    defer(() => {
                                        if (cancelled) return;
                                        setIsPeerTyping(true);
                                        if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
                                        peerTypingTimerRef.current = setTimeout(
                                            () => setIsPeerTyping(false),
                                            PEER_TYPING_TTL_MS,
                                        );
                                    });
                                    return;
                                }
                                if (data.type === 'NEW_MESSAGE' || data.type === 'MESSAGE_UPDATE') {
                                    defer(() => {
                                        if (cancelled) return;
                                        setIsPeerTyping(false);
                                        onMessageReceivedRef.current(data);
                                    });
                                }
                            } catch (error) {
                                log.error('Erro ao processar mensagem:', error);
                            }
                        },
                    });
                    connectPartySocket(socket);
                } catch (error) {
                    log.debug('PartyKit chat não inicializado:', error);
                }
            })();
        };

        const cooldown = createPartykitCooldownController('chat', connectSocket, () => cancelled);
        const debounceTimer = setTimeout(connectSocket, PARTYKIT_CHAT_DEBOUNCE_MS);
        const detachVisibility = attachPartykitVisibilityReconnect(
            'chat', () => socketRef.current, connectSocket, () => cancelled,
        );

        return () => {
            cancelled = true;
            clearTimeout(debounceTimer);
            if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
            cooldown.dispose();
            detachVisibility();
            safeClosePartySocket(socketRef.current);
            socketRef.current = null;
        };
    }, [enabled, conversationDbId]);

    return { isRealtimeConnected: isConnected, isPeerTyping, sendTyping };
}
